import { Select } from '@mantine/core';
import { useField } from '@mantine/form';
import { modals } from '@mantine/modals';
import { IconGavel } from '@tabler/icons-react';
import { ChooseAdditionalInvoices } from 'app/Private/Clients/Detail/UnhandledInvoicesTab/ChooseAdditionalInvoices';
import { useInvoiceSelection } from 'app/Private/Clients/Detail/UnhandledInvoicesTab/use-invoice-selection';
import { iconProps } from 'components/icon-props';
import { Right } from 'components/Right';
import { SaveButton } from 'components/SaveButton';
import { tKeys, useTranslate } from 'locales/index';
import { useTranslation } from 'locales/logic';
import type { InvoiceAction, UnhandledInvoice } from 'modules/AtRisk/api/use-load-unhandled-client';
import { isNullish } from 'remeda';
import { AmlModal } from 'shared/components/InvoiceActionsDropdown/AmlModal/AmlModal';
import { useLoadThirdParties } from 'shared/hooks/use-load-third-parties';
import type { ActionButtonDefinition } from '../use-build-action-buttons';
import { computeCommonProps } from './common';
import { useThirdPartySend } from './use-third-party-send';

export function useLawyer(): (
  action: InvoiceAction,
  invoices: Array<UnhandledInvoice>,
) => ActionButtonDefinition {
  const { t } = useTranslation();
  const { selectedInvoiceIds } = useInvoiceSelection();

  return (action, invoices) => {
    const handler = () =>
      modals.open({
        size: 'lg',
        title: t(tKeys.DEBTOR.REACTION.SELECT_INVOICES),
        children: (
          <ChooseAdditionalInvoices
            selectedInvoiceIds={selectedInvoiceIds}
            unhandledInvoices={invoices}
            actionType="lawyer"
            onNext={({ invoiceIds }) => {
              const unhandledIds = new Set(invoices.map(invoice => invoice.id));
              modals.closeAll();
              modals.open({
                size: 'md',
                title: t(tKeys.CHOOSE_LAWYER),
                children: (
                  <ChoseLawyer
                    invoices={invoices.filter(invoice => invoiceIds.includes(invoice.id))}
                    extraInvoiceIds={invoiceIds.filter(id => !unhandledIds.has(id))}
                  />
                ),
              });
            }}
          />
        ),
      });

    return {
      icon: <IconGavel {...iconProps()} />,
      translationKey: tKeys.THIRD_PARTY_CASES.CASE_TYPE.LAWYER,
      onClick: handler,
      ...computeCommonProps(action, invoices),
    };
  };
}

type ChoseLawyerProps = {
  invoices: Array<UnhandledInvoice>;
  extraInvoiceIds: Array<string>;
};

const ChoseLawyer = ({ invoices, extraInvoiceIds }: ChoseLawyerProps) => {
  const t = useTranslate();

  const {
    invoiceIds,
    mainInvoiceId,
    additionalInvoiceIds,
    isSendToThirdPartyLoading,
    send,
    trackAndClose,
  } = useThirdPartySend(invoices, extraInvoiceIds);
  const {
    thirdParties: { lawyer },
    isThirdPartiesInitialLoading,
  } = useLoadThirdParties({ ids: invoiceIds.map(Number) });

  const selectedLawyer = useField<string | null>({ initialValue: '' });
  const selectedLawyerId = selectedLawyer.getValue();
  const selectedLawyerCompany = lawyer.availableCompanies.find(
    company => String(company.id) === selectedLawyerId,
  );

  const shouldShowAmlForm = selectedLawyerCompany?.shouldShowAmlForm ?? false;

  const sendToLawyer = () => {
    if (isNullish(selectedLawyerId)) return;
    if (!mainInvoiceId) return;

    if (shouldShowAmlForm) {
      modals.open({
        title: t(tKeys.AML_MODAL.TITLE),
        size: 'auto',
        children: (
          <AmlModal
            invoiceIds={[Number(mainInvoiceId)]}
            thirdPartyCompanyId={Number(selectedLawyerId)}
            additionalInvoiceIds={additionalInvoiceIds}
            onSubmit={() => {}}
            onSendSuccess={trackAndClose}
          />
        ),
      });
      return;
    }

    send(selectedLawyerId);
  };

  if (isThirdPartiesInitialLoading) return <div>{t(tKeys.LOADING)}</div>;

  return (
    <>
      <Select
        label={t(tKeys.SELECT_LAWYER)}
        placeholder={t(tKeys.SELECT_LAWYER)}
        data={lawyer.availableCompanies.map(company => ({
          label: company.name,
          value: String(company.id),
        }))}
        {...selectedLawyer.getInputProps()}
      />
      <Right mt="md">
        <SaveButton
          label={t(tKeys.SUBMIT)}
          loading={isSendToThirdPartyLoading}
          disabled={selectedLawyer.getValue() === ''}
          onClick={sendToLawyer}
        />
      </Right>
    </>
  );
};
